import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar'
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'
import { DirectivesModule } from '../../core/directives/directives.module'

@Component({
  selector: 'app-fc-select-multiple-paged',
  templateUrl: './fc-select-multiple-paged.component.html',
  styleUrls: ['./fc-select-multiple-paged.component.scss', '../../../styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FcSelectMultiplePagedComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    DirectivesModule,
    FormsModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    NgScrollbarModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class FcSelectMultiplePagedComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  @Input() public placeholder: string = 'Select options'
  @Input() public searchPlaceholder: string = ''
  @Input() public service: any
  @Input() public control: AbstractControl | null = null
  @Input() public showImage: boolean = false
  @Input() public fixDropdownDirection: string = ''
  @Input() public searchProperty: string = 'nameContains'
  @Input() public maxVisibleChips: number = 2
  @Input() public ignoredIds: string[] = []
  @Input() public isRequired: boolean = false

  @ViewChild('inputElement') public inputElement!: ElementRef<HTMLInputElement>

  public options = signal<any[]>([])
  public isLoading = signal<boolean>(false)
  public isOpen = signal<boolean>(false)
  public isOptionsAbove: boolean = false
  public isOnLastPage: boolean = true
  public shouldLoadMoreItems: boolean = true
  public searchTerm = new FormControl('')

  /**
   * Internal selected items array (used as ControlValueAccessor state)
   */
  public selectedItems = signal<any[]>([])

  private elementRef = inject(ElementRef)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly destroy$ = new Subject<void>()
  private isCursorPagination: boolean = false
  private searchTermObservable$!: Observable<any>
  private searchTermSubscription!: Subscription

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: any[]) => void = () => {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {}

  public get isDisabled(): boolean {
    return this.control?.disabled ?? false
  }

  public get isInvalid(): boolean {
    return (this.control?.invalid && this.control?.dirty) ?? false
  }

  public get isPristine(): boolean {
    return this.control?.pristine ?? true
  }

  public ngOnChanges(): void {
    if (!this.service) {
      return
    }

    this.searchTermObservable$ = this.getSearchTermObservable$()
    this.setSearchTermSubscriptions()
    this.isCursorPagination = Boolean(this.service.after)

    if (this.control) {
      if (!this.isRequired) {
        this.isRequired =
          Boolean(this.control.validator) &&
          Boolean(this.control.validator?.({} as FormControl)?.['required'])
      }

      this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any[]) => {
        if (Array.isArray(value)) {
          this.selectedItems.set(value)
        } else {
          this.selectedItems.set([])
        }

        this.cdr.detectChanges()
      })

      // Sync initial value
      const currentValue = this.control.value
      if (Array.isArray(currentValue)) {
        this.selectedItems.set(currentValue)
      }
    }

    this.service.shouldUpdateOptions$?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setSearchTermSubscriptions()
      this.options.set([])
    })
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()

    if (this.isCursorPagination) {
      this.service.after?.set(null)
      return
    }

    this.service?.offset?.update(() => 0)
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────

  public writeValue(value: any[]): void {
    const items = Array.isArray(value) ? value : []
    this.selectedItems.set(items)
    this.cdr.detectChanges()
  }

  public registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control?.disable()
    } else {
      this.control?.enable()
    }

    this.cdr.detectChanges()
  }

  // ─── Dropdown ────────────────────────────────────────────────────────────

  public toggleDropdown(): void {
    if (this.isDisabled) {
      return
    }

    if (!this.options().length) {
      this.searchTerm.updateValueAndValidity()
    }

    if (this.fixDropdownDirection === 'up') {
      this.isOptionsAbove = true
      this.isOpen.set(!this.isOpen())
      return
    }

    if (this.fixDropdownDirection === 'down') {
      this.isOptionsAbove = false
      this.isOpen.set(!this.isOpen())
      return
    }

    const componentRect = this.elementRef.nativeElement.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const spaceBelow = windowHeight - componentRect.bottom
    const optionsListHeight = 262

    this.isOptionsAbove = spaceBelow < optionsListHeight || spaceBelow < componentRect.top
    this.isOpen.set(!this.isOpen())

    if (this.isOpen()) {
      setTimeout(() => {
        this.inputElement?.nativeElement.focus()
      }, 0)
    }
  }

  // ─── Selection ───────────────────────────────────────────────────────────

  public isSelected(item: any): boolean {
    return this.selectedItems().some((selected) => selected?.id === item?.id)
  }

  public toggleItem(item: any): void {
    const current = this.selectedItems()
    let next: any[]

    if (this.isSelected(item)) {
      next = current.filter((selected) => selected?.id !== item?.id)
    } else {
      next = [...current, item]
    }

    this.selectedItems.set(next)
    this.onChange(next)
    this.onTouched()

    if (this.control) {
      this.control.setValue(next)
      this.control.markAsDirty()
    }

    this.cdr.detectChanges()
  }

  public removeChip(item: any): void {
    const next = this.selectedItems().filter((selected) => selected?.id !== item?.id)
    this.selectedItems.set(next)
    this.onChange(next)
    this.onTouched()

    if (this.control) {
      this.control.setValue(next)
      this.control.markAsDirty()
    }

    this.cdr.detectChanges()
  }

  public clearAll(): void {
    this.selectedItems.set([])
    this.onChange([])
    this.onTouched()

    if (this.control) {
      this.control.setValue([])
      this.control.markAsDirty()
    }

    this.cdr.detectChanges()
  }

  // ─── Scroll / Pagination ─────────────────────────────────────────────────

  public onScrollbarUpdate(scrollbar: NgScrollbar): void {
    scrollbar.scrolled.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      this.onScroll(event)
    })
  }

  public onScroll(event: any): void {
    const scrollPosition: number = event.target.scrollTop
    const scrollHeight: number = event.target.scrollHeight
    const clientHeight: number = event.target.clientHeight
    const isAtBottom: boolean = scrollPosition + clientHeight >= scrollHeight

    if (!isAtBottom || this.isLoading() || !this.shouldLoadMoreItems) {
      return
    }

    this.isLoading.set(true)

    if (this.isCursorPagination) {
      this.service.after.set(this.service.pageInfo()?.endCursor)
    } else {
      this.service?.offset.update((value: number) => value + (this.service?.limit() as number))
    }

    from(this.service?.searchPage())
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(async ({ data }: any) => {
        await this.setOptions(data)
        this.isLoading.set(false)
      })
  }

  public async setOptions(data: any): Promise<void> {
    const firstProperty: any = Object.values(data)[0]
    const pageInfo = firstProperty?.pageInfo

    this.service.pageInfo?.set(firstProperty?.pageInfo)
    this.isOnLastPage = this.isCursorPagination ? !pageInfo?.startCursor : !pageInfo?.hasNextPage

    if (this.isOnLastPage && !this.options().length) {
      this.options.set(firstProperty?.items)
      return
    }

    this.options.set([...this.options(), ...firstProperty.items])
    this.shouldLoadMoreItems = this.options().length !== firstProperty?.totalCount
  }

  public trackByFn(index: number, item: any): any {
    return item?.id ?? index
  }

  @HostListener('document:click', ['$event'])
  public onClick(event: any): void {
    const clickedInside = event.composedPath().includes(this.elementRef.nativeElement)

    if (!clickedInside) {
      this.isOpen.set(false)
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private setSearchTermSubscriptions(): void {
    if (this.searchTermSubscription) {
      this.searchTermSubscription.unsubscribe()
    }

    this.subscribeToSearchTermChanges()
  }

  private subscribeToSearchTermChanges(): void {
    this.searchTermSubscription = this.searchTermObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ({ data }: any) => {
        this.isLoading.set(false)
        await this.setOptions(data)
      })
  }

  private getSearchTermObservable$(): Observable<any> {
    return this.searchTerm.valueChanges.pipe(
      distinctUntilChanged(),
      tap(() => {
        this.isLoading.set(true)
      }),
      debounceTime(500),
      tap(() => {
        this.options.set([])
        this.isOnLastPage = false

        if (this.isCursorPagination) {
          this.service.after?.set(null)
          return
        }

        this.service?.offset.update(() => 0)
      }),
      filter((value) => typeof value === 'string'),
      map((value) => value?.trim().toLowerCase()),
      switchMap(() => {
        this.service?.filterForm?.get(this.searchProperty)?.setValue(this.searchTerm?.value)
        return this.service?.searchPage()
      }),
      takeUntil(this.destroy$)
    )
  }
}
